import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { subDays, startOfDay, endOfDay, format } from 'date-fns';
import MetricsGrid from '../../../components/admin/notifications/MetricsGrid';
import EngagementChart from '../../../components/admin/notifications/EngagementChart';
import HeatmapView from '../../../components/admin/notifications/HeatmapView';
import FunnelAnalysis from '../../../components/admin/notifications/FunnelAnalysis';
import ExportControls from '../../../components/admin/notifications/ExportControls';
import { NotificationAnalyticsService } from '../../../services/notificationAnalyticsService';
import {
  NotificationMetrics,
  NotificationTrendData,
  NotificationVolumeByTime,
  NotificationVolumeByDay,
  DateRange,
} from '../../../types/notification-admin';

type DatePreset = '7d' | '30d' | '90d' | 'custom';

/**
 * NotificationAnalytics Page
 * Comprehensive analytics dashboard with metrics, charts, and export functionality
 *
 * Features:
 * - Overall metrics (sent, delivered, read, actioned)
 * - Engagement trend charts
 * - Time-based heatmaps
 * - Conversion funnel analysis
 * - Data export (CSV, JSON, PDF)
 * - Date range filtering
 */
const NotificationAnalytics: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    start: startOfDay(subDays(new Date(), 30)),
    end: endOfDay(new Date()),
  });

  // Analytics data
  const [metrics, setMetrics] = useState<NotificationMetrics | null>(null);
  const [trendData, setTrendData] = useState<NotificationTrendData[]>([]);
  const [hourlyData, setHourlyData] = useState<NotificationVolumeByTime[]>([]);
  const [dailyData, setDailyData] = useState<NotificationVolumeByDay[]>([]);

  useEffect(() => {
    loadAnalytics();
  }, [datePreset, customDateRange]);

  const getDateRange = (): DateRange => {
    const now = new Date();
    switch (datePreset) {
      case '7d':
        return {
          start: startOfDay(subDays(now, 7)),
          end: endOfDay(now),
        };
      case '30d':
        return {
          start: startOfDay(subDays(now, 30)),
          end: endOfDay(now),
        };
      case '90d':
        return {
          start: startOfDay(subDays(now, 90)),
          end: endOfDay(now),
        };
      case 'custom':
        return customDateRange;
      default:
        return {
          start: startOfDay(subDays(now, 30)),
          end: endOfDay(now),
        };
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateRange = getDateRange();

      // Load all analytics data in parallel
      const [metricsData, trendsData, hourlyVolumeData, dailyVolumeData] = await Promise.all([
        NotificationAnalyticsService.getOverallMetrics(dateRange),
        NotificationAnalyticsService.getTrendData(dateRange, 'day'),
        NotificationAnalyticsService.getVolumeByTimeOfDay(),
        NotificationAnalyticsService.getVolumeByDayOfWeek(),
      ]);

      setMetrics(metricsData);
      setTrendData(trendsData);
      setHourlyData(hourlyVolumeData);
      setDailyData(dailyVolumeData);
    } catch (err: any) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleDatePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
  };

  const handleCustomDateChange = (field: 'start' | 'end', date: Date | null) => {
    if (date) {
      setCustomDateRange((prev) => ({
        ...prev,
        [field]: field === 'start' ? startOfDay(date) : endOfDay(date),
      }));
    }
  };

  const handleRefresh = () => {
    loadAnalytics();
  };

  const prepareExportData = (): any[] => {
    if (!metrics || !trendData) return [];

    return trendData.map((item) => ({
      Date: item.date.toISOString(),
      Sent: item.sent,
      Delivered: item.delivered,
      Read: item.read,
      Actioned: item.actioned,
      'Delivery Rate': `${((item.delivered / item.sent) * 100).toFixed(1)}%`,
      'Read Rate': `${((item.read / item.delivered) * 100).toFixed(1)}%`,
      'Action Rate': `${((item.actioned / item.read) * 100).toFixed(1)}%`,
    }));
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/notifications')}
          sx={{ mb: 2 }}
        >
          Back to Notification Dashboard
        </Button>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom>
            Notification Analytics
          </Typography>

          <Box display="flex" gap={2} alignItems="center">
            <ExportControls
              data={prepareExportData()}
              filename="notification-analytics"
              disabled={loading || !metrics}
            />
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary">
          Comprehensive analytics and insights into notification performance and user engagement
        </Typography>
      </Box>

      {/* Date Range Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <CalendarIcon color="action" />
          <Typography variant="subtitle2">Date Range:</Typography>

          <ToggleButtonGroup
            value={datePreset}
            exclusive
            onChange={(_, value) => value && handleDatePresetChange(value)}
            size="small"
          >
            <ToggleButton value="7d">Last 7 Days</ToggleButton>
            <ToggleButton value="30d">Last 30 Days</ToggleButton>
            <ToggleButton value="90d">Last 90 Days</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          {datePreset === 'custom' && (
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Start Date"
                type="date"
                value={format(customDateRange.start, 'yyyy-MM-dd')}
                onChange={(e) => handleCustomDateChange('start', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End Date"
                type="date"
                value={format(customDateRange.end, 'yyyy-MM-dd')}
                onChange={(e) => handleCustomDateChange('end', e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Key Metrics
        </Typography>
        <MetricsGrid metrics={metrics} loading={loading} />
      </Box>

      {/* Engagement Trends */}
      <Box sx={{ mb: 3 }}>
        <EngagementChart data={trendData} loading={loading} />
      </Box>

      {/* Conversion Funnel */}
      <Box sx={{ mb: 3 }}>
        <FunnelAnalysis metrics={metrics} loading={loading} />
      </Box>

      {/* Heatmaps */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Notification Patterns
        </Typography>
        <HeatmapView hourlyData={hourlyData} dailyData={dailyData} loading={loading} />
      </Box>

      {/* Additional Insights */}
      {metrics && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Performance Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Delivery Success
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {metrics.deliveryRate.toFixed(1)}%
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    User Engagement (Read Rate)
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={metrics.readRate >= 70 ? 'success.main' : 'warning.main'}
                  >
                    {metrics.readRate.toFixed(1)}%
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Action Conversion
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="info.main">
                    {metrics.actionRate.toFixed(1)}%
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time to Read
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {Math.round(metrics.avgTimeToRead)} minutes
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              <Box sx={{ mt: 2 }}>
                {metrics.deliveryRate < 95 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Delivery rate is below 95%. Check for failed deliveries and system issues.
                  </Alert>
                )}
                {metrics.readRate < 50 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Read rate is below 50%. Consider improving notification relevance and timing.
                  </Alert>
                )}
                {metrics.actionRate < 10 && metrics.totalActioned > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Action rate is low. Review your call-to-action buttons and messaging.
                  </Alert>
                )}
                {metrics.deliveryRate >= 95 && metrics.readRate >= 70 && (
                  <Alert severity="success">
                    Great job! Your notifications are performing well across all metrics.
                  </Alert>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default NotificationAnalytics;
